import { normalizeEmotion } from "../../utils/emotion";
import { useI18n } from "../../utils/i18n";
import { useRef, useState, useEffect } from "react";
import { CONTENT_H, CONTENT_W, PANEL_H, PANEL_OFFSET_X, PANEL_OFFSET_Y, PANEL_W } from "./chunk-0.jsx";
import { MiaobanEyes } from "./chunk-5.jsx";
import { RoboEyes } from "./chunk-6.jsx";
import { RawEyes } from "./chunk-7.jsx";
import { ClassicEyes } from "./chunk-8.jsx";
import { ChamiiCuteEyes } from "./chunk-10.jsx";
import { OledEyes } from "./chunk-12.jsx";
import { AniEyes } from "./chunk-17.jsx";
import { Esp32Eyes } from "./chunk-18.jsx";
import { UncannyEyes } from "./chunk-19.jsx";
import { RoundLcdEyes } from "./chunk-21.jsx";
export default function DeviceScreenPreview({
  theme,
  variant,
  emotion,
  expression,
  poke = 0,
  accent,
  motionBoost = false,
  contentOnly = false,
  showCaption = false,
  className = ''
}) {
  const e = normalizeEmotion(emotion);
  const {
    tx
  } = useI18n();
  const slotRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [isInView, setIsInView] = useState(true);
  const heartbeatRef = useRef(0);
  const [animAgeMs, setAnimAgeMs] = useState(null);
  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;
    const basisW = contentOnly ? CONTENT_W : PANEL_W;
    const apply = w => {
      if (!w || w <= 0) return;
      // Render the full panel (320×240), or show only the visible content (280×240).
      // Scale is based on the chosen width so previews stay centered.
      const next = Math.min(1, w / basisW);
      setScale(prev => Math.abs(prev - next) < 0.01 ? prev : next);
    };
    const update = () => apply(el.getBoundingClientRect().width);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const ro = new ResizeObserver(entries => {
      const w = entries?.[0]?.contentRect?.width;
      if (typeof w === 'number' && w > 0) apply(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [contentOnly]);
  useEffect(() => {
    const el = slotRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(entries => {
      const ent = entries?.[0];
      if (!ent) return;
      setIsInView(Boolean(ent.isIntersecting || ent.intersectionRatio > 0));
    }, {
      threshold: 0.01
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const basisW = contentOnly ? CONTENT_W : PANEL_W;
  const basisOffsetX = contentOnly ? 0 : PANEL_OFFSET_X;
  const scaledW = Math.round(basisW * scale);
  const scaledH = Math.round(PANEL_H * scale);
  useEffect(() => {
    if (!showCaption) return;
    const t = setInterval(() => {
      const last = heartbeatRef.current || 0;
      setAnimAgeMs(last ? Date.now() - last : null);
    }, 500);
    return () => clearInterval(t);
  }, [showCaption]);
  const animLabel = (() => {
    if (!showCaption) return '';
    const rafThemes = ['robo', 'raw', 'ani', 'esp32', 'uncanny', 'round', 'miaoban'];
    if (theme === 'classic' || theme === 'oled' || theme === 'chamii') return tx({
      zh: 'CSS 动画',
      en: 'CSS animation'
    });
    if (!rafThemes.includes(theme)) return tx({
      zh: '静态主题',
      en: 'Static theme'
    });
    if (animAgeMs == null) return tx({
      zh: '启动中…',
      en: 'Starting…'
    });
    return animAgeMs < 1500 ? tx({
      zh: '运行中',
      en: 'Running'
    }) : tx({
      zh: '已暂停/不可见',
      en: 'Paused / not visible'
    });
  })();
  return <div ref={slotRef} className={`w-full flex justify-center ${className}`}>
      <div className="relative">
        <div className="rounded-[20px] bg-black border border-white/10 shadow-[0_10px_30px_rgba(0,0,0,0.35)] overflow-hidden" style={{
        width: scaledW,
        height: scaledH
      }}>
          <div className="relative" style={{
          width: basisW,
          height: PANEL_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left'
        }}>
            {/* 240×240 方屏：content == panel；如后续加 1.83 profile，则这里会出现 gap_x 区域。 */}
            {!contentOnly ? <div className="absolute inset-y-0 left-0" style={{
            width: PANEL_OFFSET_X,
            background: 'rgba(255,255,255,0.03)'
          }} /> : null}
            <div className="absolute" style={{
            left: basisOffsetX,
            top: PANEL_OFFSET_Y,
            width: CONTENT_W,
            height: CONTENT_H
          }}>
              {theme === 'robo' && <RoboEyes emotion={e} poke={poke} heartbeatRef={heartbeatRef} accent={accent} active={isInView} motionBoost={motionBoost} />}
              {theme === 'raw' && <RawEyes emotion={e} poke={poke} heartbeatRef={heartbeatRef} accent={accent} active={isInView} motionBoost={motionBoost} />}
              {theme === 'chamii' && <ChamiiCuteEyes emotion={e} variant={variant} accent={accent} poke={poke} active={isInView} motionBoost={motionBoost} />}
              {theme === 'miaoban' && <MiaobanEyes emotion={e} variant={variant} poke={poke} heartbeatRef={heartbeatRef} active={isInView} motionBoost={motionBoost} />}
              {theme === 'classic' && <ClassicEyes emotion={e} accent={accent} poke={poke} />}
              {theme === 'oled' && <OledEyes emotion={e} variant={variant} poke={poke} />}
              {theme === 'ani' && <AniEyes emotion={e} heartbeatRef={heartbeatRef} active={isInView} motionBoost={motionBoost} />}
              {theme === 'esp32' && <Esp32Eyes emotion={e} expression={expression} variant={variant} poke={poke} heartbeatRef={heartbeatRef} active={isInView} motionBoost={motionBoost} />}
              {theme === 'uncanny' && <UncannyEyes emotion={e} variant={variant} poke={poke} heartbeatRef={heartbeatRef} accent={accent} active={isInView} motionBoost={motionBoost} />}
              {theme === 'round' && <RoundLcdEyes emotion={e} variant={variant} poke={poke} heartbeatRef={heartbeatRef} accent={accent} active={isInView} motionBoost={motionBoost} />}
            </div>
          </div>
        </div>
        {showCaption ? <div className="mt-2 text-center text-[11px] text-slate-500">
            {contentOnly ? tx({
          zh: '可视 {cw}×{ch} · 动画：{a}',
          en: 'Visible {cw}×{ch} · Anim: {a}'
        }, {
          cw: CONTENT_W,
          ch: CONTENT_H,
          a: animLabel
        }) : tx({
          zh: '可视 {cw}×{ch}（面板 {pw}×{ph}，gap_x={gx}）· 动画：{a}',
          en: 'Visible {cw}×{ch} (panel {pw}×{ph}, gap_x={gx}) · Anim: {a}'
        }, {
          cw: CONTENT_W,
          ch: CONTENT_H,
          pw: PANEL_W,
          ph: PANEL_H,
          gx: PANEL_OFFSET_X,
          a: animLabel
        })}
          </div> : null}
      </div>
    </div>;
}
